import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import LandingDemo from "../components/LandingDemo";
import { startGoogleLogin } from "../lib/auth";
import {
  LineChart, Sparkles, BellRing, FileText, CalendarClock, ScanLine,
  ShieldCheck, Lock, CheckCircle2, ArrowRight, Menu, X, Star, Mail, Send, Loader2, Users, ChevronDown,
  Droplet, HeartPulse, BarChart3, Mic, UserPlus, ChevronRight,
  QrCode, CalendarDays, MonitorSmartphone, LifeBuoy,
} from "lucide-react";

const BLUE = "#2563AE";

const ABOUT_QUOTE = "En ClinPed, desarrollamos herramientas creadas por profesionales de la salud para profesionales de la salud. Entendemos las necesidades del médico y nos comprometemos a ofrecer una experiencia digital intuitiva, segura e innovadora que acompañe su práctica diaria, siempre en beneficio de sus pacientes.";
const ABOUT_SEALS = [[ShieldCheck, "NOM-004 / 024"], [Lock, "Datos cifrados"], [Sparkles, "Soporte médico"]];

const ECOSYSTEM = [
  { icon: Droplet, color: "#0d9488", name: "ClinIV", sub: "Soluciones Intravenosas Pediátricas", desc: "Cálculos precisos de líquidos, electrolitos y requerimientos según peso.", bullets: ["Volumen por peso", "Electrolitos", "Requerimientos", "Alertas de seguridad"] },
  { icon: HeartPulse, color: "#e11d48", name: "ClinPulse", sub: "Infusiones y Vasoactivos Inteligentes", desc: "Calculadora de infusiones vasoactivas con dosis, diluciones y velocidades seguras.", bullets: ["Dosis por peso o SC", "Preparación automática", "Rangos terapéuticos", "Cálculo paso a paso"] },
  { icon: BarChart3, color: "#16a34a", name: "ClinPed Analytics", sub: "Tu consulta en números", desc: "Indicadores clave para entender y mejorar tu práctica.", bullets: ["KPIs de consulta", "Motivos y diagnósticos frecuentes", "Tiempo de consulta", "Tendencia mensual"] },
  { icon: Mic, color: "#7c3aed", name: "ClinPed Voice", sub: "Dictado clínico con IA", desc: "Habla y la IA transcribe y llena la historia clínica por apartados.", bullets: ["Dictado en tiempo real", "Notas y evolución", "Diagnóstico y tratamiento", "Ahorra tiempo en cada nota"] },
  { icon: Users, color: "#ea580c", name: "Accesos Delegados", sub: "Asistente y Enfermería", desc: "Comparte accesos QR + PIN para que tu equipo trabaje desde su propio teléfono.", bullets: ["Sin ver datos clínicos", "Agenda y citas", "Signos vitales y somato", "Seguro y controlado"] },
];

const HOW_STEPS = [
  { icon: UserPlus, title: "Consulta", desc: "Crea o busca al paciente y abre su expediente." },
  { icon: Mic, title: "Dicta o registra", desc: "Usa ClinPed Voice o registra la consulta manualmente." },
  { icon: HeartPulse, title: "Calcula y decide", desc: "Usa ClinIV y ClinPulse para cálculos precisos y seguros." },
  { icon: BarChart3, title: "Analiza y mejora", desc: "Revisa tus indicadores en ClinPed Analytics." },
  { icon: FileText, title: "Comparte y colabora", desc: "Tu equipo apoya desde su teléfono con accesos delegados." },
];

const startLogin = () => {
  // Login nativo con Google (cliente OAuth propio, con scope calendar.events).
  startGoogleLogin();
};

const FEATURES = [
  { icon: LineChart, title: "Percentiles y somatometría", desc: "Gráficas CDC y OMS automáticas con trayectoria de crecimiento. Adiós a los cálculos manuales." },
  { icon: Sparkles, title: "Asistente con IA", desc: "Cálculo de dosis por peso, dictado por voz y sugerencias clínicas al instante." },
  { icon: BellRing, title: "Alarmas de signos vitales", desc: "Alertas inmediatas cuando un signo vital está fuera de rango para la edad." },
  { icon: FileText, title: "Recetas y consentimientos", desc: "Genera PDF profesionales con tu firma, sello y código QR verificable." },
  { icon: CalendarClock, title: "Agenda inteligente", desc: "Organiza tus citas y envía recordatorios al tutor por WhatsApp en un clic." },
  { icon: ScanLine, title: "Análisis de estudios", desc: "Sube laboratorios o imágenes y recibe una lectura asistida por IA." },
];

const TESTIMONIALS = [
  { name: "Dr. Edgar Camacho", role: "Pediatra", text: "ClinPed cambió la forma en que trabajo. Ahorro horas cada semana y mis expedientes están impecables.", img: "https://images.unsplash.com/photo-1612531385446-f7e6d131e1d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" },
  { name: "Dra. Diana Arlet", role: "Pediatra neonatóloga", text: "Las gráficas automáticas y el cálculo de dosis por peso son una maravilla. Todo en un solo lugar.", img: "https://images.pexels.com/photos/6749778/pexels-photo-6749778.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200" },
  { name: "Dr. Jesús López", role: "Odontólogo Pediatra", text: "La mejor inversión para mi consultorio. Los papás quedan encantados con los recordatorios por WhatsApp.", img: "https://images.unsplash.com/photo-1645066928295-2506defde470?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" },
];

const PLAN_BASIC = ["Pacientes ilimitados", "Asistente clínico con IA", "Recetas y PDF con código QR", "Somatometría CDC/OMS automática", "Agenda y recordatorios WhatsApp", "Adjuntos y estudios ilimitados", "Exportación HL7 CDA / XML", "Perfil en el directorio público de médicos", "Cumplimiento NOM-004 y NOM-024"];
const PLAN_PRO = ["Todo lo del plan Básico", "Apps de Asistente y Enfermería (QR + PIN)", "Sala de espera: signos vitales listos antes de pasar", "Dictado con IA: graba la consulta y arma la nota", "Oci por voz (agenda y dicta hablando)", "Revisión final del expediente según la norma", "Perfil destacado en el directorio público de médicos", "Soporte prioritario"];
const PLAN_DENTAL = ["Todo lo del plan Básico", "Odontograma digital interactivo", "Periodoncia: sondaje de 6 sitios por diente", "Ortodoncia: seguimiento con fotos Antes/Después", "Compartir el odontograma por QR / PDF", "Perfil en el directorio público de médicos", "Escala a Pro cuando quieras"];

function Nav() {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mAboutOpen, setMAboutOpen] = useState(false);
  const links = [["Herramientas", "ecosistema"], ["Características", "caracteristicas"], ["Demo", "demo"], ["Precios", "precios"], ["Contacto", "contacto"]];
  const go = (id) => { setOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/60">
      <nav className="max-w-6xl mx-auto flex items-center justify-between px-5 md:px-8 h-16">
        <Logo size={38} />
        <div className="hidden md:flex items-center gap-8">
          {links.map(([label, id]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)}
              className="text-sm font-semibold text-brand-ink/70 hover:text-brand-ink transition-colors">{label}</button>
          ))}
          <div className="relative" onMouseEnter={() => setAboutOpen(true)} onMouseLeave={() => setAboutOpen(false)}>
            <button data-testid="nav-nosotros" onClick={() => setAboutOpen((v) => !v)}
              className="flex items-center gap-1 text-sm font-semibold text-brand-ink/70 hover:text-brand-ink transition-colors">
              Nosotros <ChevronDown size={15} className={`transition-transform ${aboutOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {aboutOpen && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18 }}
                  data-testid="about-dropdown"
                  className="absolute right-0 top-full mt-3 w-[440px] bg-white rounded-2xl shadow-xl border border-stone-200/70 p-5 z-50">
                  <p className="uppercase text-[10px] tracking-[0.25em] font-bold text-brand-terracotta mb-3">Nosotros · Hecho por médicos</p>
                  <div className="flex gap-4">
                    <img src="/nosotros-equipo.jpg" data-testid="about-team-photo" alt="Equipo ClinPed"
                      className="w-20 h-20 object-cover rounded-xl ring-1 ring-stone-200/70 shrink-0" />
                    <blockquote className="relative text-[13px] leading-relaxed text-brand-ink/55 font-light italic">
                      <span className="absolute -top-3 -left-1 text-3xl leading-none font-serif text-brand-terracotta/30">“</span>
                      <span className="block pl-4">{ABOUT_QUOTE}”</span>
                    </blockquote>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-stone-100">
                    {ABOUT_SEALS.map(([Icon, label], i) => (
                      <span key={i} data-testid={`trust-seal-${i}`} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-ink/70 bg-stone-50 rounded-full px-2.5 py-1">
                        <Icon size={12} style={{ color: BLUE }} /> {label}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button data-testid="nav-directory" onClick={() => (window.location.href = "/directorio")}
            className="text-sm font-semibold text-teal-600 hover:text-teal-700 transition-colors">Contacta a tu médico</button>
          <button data-testid="nav-login-btn" onClick={startLogin}
            className="text-white text-sm font-outfit font-semibold px-5 py-2.5 rounded-xl shadow-md hover:-translate-y-0.5 transition-all"
            style={{ backgroundColor: BLUE }}>Iniciar sesión</button>
        </div>
        <button className="md:hidden text-brand-ink" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Menú">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>
      {open && (
        <div className="md:hidden bg-white border-t border-stone-100 px-5 py-4 flex flex-col gap-3">
          {links.map(([label, id]) => (
            <button key={id} data-testid={`nav-m-${id}`} onClick={() => go(id)} className="text-left text-brand-ink font-semibold py-1">{label}</button>
          ))}
          <button data-testid="nav-m-nosotros" onClick={() => setMAboutOpen((v) => !v)}
            className="flex items-center justify-between text-left text-brand-ink font-semibold py-1">
            Nosotros <ChevronDown size={16} className={`transition-transform ${mAboutOpen ? "rotate-180" : ""}`} />
          </button>
          {mAboutOpen && (
            <div data-testid="about-dropdown-mobile" className="bg-stone-50 rounded-2xl p-4 -mt-1">
              <div className="flex gap-3">
                <img src="/nosotros-equipo.jpg" alt="Equipo ClinPed" className="w-16 h-16 object-cover rounded-lg ring-1 ring-stone-200/70 shrink-0" />
                <blockquote className="relative text-[12px] leading-relaxed text-brand-ink/55 font-light italic">
                  <span className="block pl-3">“{ABOUT_QUOTE}”</span>
                </blockquote>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {ABOUT_SEALS.map(([Icon, label], i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-ink/70 bg-white rounded-full px-2.5 py-1">
                    <Icon size={12} style={{ color: BLUE }} /> {label}
                  </span>
                ))}
              </div>
            </div>
          )}
          <button data-testid="nav-m-directory" onClick={() => (window.location.href = "/directorio")} className="text-left text-teal-600 font-semibold py-1">Contacta a tu médico</button>
          <button data-testid="nav-m-login-btn" onClick={startLogin} className="text-white font-semibold py-2.5 rounded-xl mt-1" style={{ backgroundColor: BLUE }}>Iniciar sesión</button>
        </div>
      )}
    </header>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  // Solo los que ya PAGARON (o admin) entran directo a la app. Los de prueba gratis ven la portada.
  useEffect(() => {
    if (user && (user.role === "admin" || user.has_paid)) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Escribe tu nombre y correo"); return; }
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success("¡Gracias! Te contactaremos pronto.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("No se pudo enviar. Intenta de nuevo."); }
    finally { setSending(false); }
  };

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#FDFBF7] font-nunito text-brand-ink overflow-x-hidden">
      <Nav />

      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 px-5 md:px-8">
        <div className="absolute -top-20 right-0 w-[28rem] h-[28rem] rounded-full bg-brand-sage/20 blur-3xl -z-0" />
        <div className="absolute top-40 -left-24 w-96 h-96 rounded-full bg-brand-terracotta/15 blur-3xl -z-0" />
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="uppercase text-xs tracking-[0.25em] font-bold text-brand-terracotta mb-4">Expediente pediátrico inteligente</p>
            <h1 className="font-outfit font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-ink">
              Tu consultorio pediátrico, <span style={{ color: BLUE }}>automatizado</span> y sin papel.
            </h1>
            <p className="mt-6 text-lg text-brand-ink/70 max-w-xl">
              Expedientes al instante, gráficas de percentiles automáticas e inteligencia artificial integrada.
              Dedica más tiempo a tus pacientes y menos a la pantalla.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button data-testid="hero-cta-trial" onClick={startLogin}
                className="flex items-center gap-2 text-white font-outfit font-semibold px-7 py-3.5 rounded-xl shadow-lg hover:-translate-y-1 transition-all"
                style={{ backgroundColor: BLUE }}>
                Prueba gratis 20 días <ArrowRight size={18} />
              </button>
              <button data-testid="hero-cta-pricing" onClick={() => document.getElementById("precios")?.scrollIntoView({ behavior: "smooth" })}
                className="font-outfit font-semibold px-7 py-3.5 rounded-xl border border-stone-300 bg-white hover:bg-stone-50 hover:-translate-y-1 transition-all">
                Ver planes y precios
              </button>
            </div>
            <p className="mt-4 text-sm text-brand-ink/50 flex items-center gap-2"><CheckCircle2 size={15} className="text-brand-sage" /> Sin tarjeta. Cancela cuando quieras.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="relative">
            <img src="https://images.unsplash.com/photo-1708687045000-9dbb586ad1e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Pediatra usando ClinPed" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" data-testid="hero-image" />
            <div className="absolute -bottom-6 -left-4 w-48 sm:w-60 rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden bg-white">
              <img src="/store/play_1_dashboard.png" alt="App ClinPed" className="w-full" />
            </div>
            <div className="absolute -top-4 -right-3 bg-white rounded-2xl shadow-xl px-4 py-3 ring-1 ring-black/5 hidden sm:flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-brand-sage/15 flex items-center justify-center"><LineChart size={18} className="text-brand-sageDark" /></div>
              <div><p className="text-xs font-bold text-brand-ink leading-none">Percentil P50</p><p className="text-[11px] text-brand-ink/50 mt-1">Peso para la edad</p></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM -> SOLUTION */}
      <section className="px-5 md:px-8 py-14">
        <div className="max-w-5xl mx-auto bg-brand-ink rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
          <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-brand-terracotta/25 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-outfit font-bold text-2xl md:text-3xl leading-tight">¿Cansado de expedientes en papel y cálculos manuales?</h2>
              <p className="mt-4 text-white/70">Perder tiempo trazando percentiles a mano, recetas ilegibles y notas que no cumplen la norma. Eso se acabó.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <p className="font-outfit font-semibold text-brand-sand text-lg">La solución</p>
              <p className="mt-2 text-white/85">ClinPed automatiza somatometría, recetas y consentimientos. Su IA y las alarmas de signos vitales te ayudan a dar el mejor cuidado, en menos tiempo y con cumplimiento NOM-004.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="caracteristicas" className="px-5 md:px-8 py-16 md:py-24 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="uppercase text-xs tracking-[0.25em] font-bold text-brand-terracotta mb-3">Características</p>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl tracking-tight">Todo lo que tu consultorio necesita, en un solo lugar</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                data-testid={`feature-${i}`} className="bg-white rounded-2xl p-7 border border-stone-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${BLUE}14` }}>
                  <f.icon size={24} style={{ color: BLUE }} />
                </div>
                <h3 className="font-outfit font-bold text-lg text-brand-ink">{f.title}</h3>
                <p className="mt-2 text-brand-ink/65 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 rounded-3xl overflow-hidden shadow-xl ring-1 ring-black/5 max-w-4xl mx-auto">
            <img src="/store/tab_4_graficas.png" alt="Gráficas de crecimiento en ClinPed" className="w-full" data-testid="features-mockup" />
          </div>
        </div>
      </section>

      {/* DEMO */}
      <LandingDemo />


      {/* ECOSISTEMA */}
      <section id="ecosistema" className="px-5 md:px-8 py-16 md:py-24 scroll-mt-20" data-testid="ecosystem-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-outfit font-bold text-3xl md:text-4xl tracking-tight text-brand-ink">
              Ecosistema <span style={{ color: "#0d9488" }}>ClinPed</span>
            </h2>
            <p className="mt-3 text-lg text-brand-ink/60">Herramientas clínicas inteligentes diseñadas para pediatras.</p>
          </motion.div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {ECOSYSTEM.map((t, i) => (
              <motion.div key={t.name} data-testid={`ecosystem-card-${i}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
                className="bg-white rounded-2xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.05)] hover:-translate-y-1 transition-transform">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${t.color}15` }}>
                  <t.icon size={26} style={{ color: t.color }} />
                </div>
                <h3 className="font-outfit font-extrabold text-lg" style={{ color: t.color }}>{t.name}</h3>
                <p className="text-xs font-semibold text-brand-ink/70 mt-0.5 mb-2">{t.sub}</p>
                <p className="text-[13px] text-brand-ink/55 leading-relaxed mb-4">{t.desc}</p>
                <ul className="space-y-1.5">
                  {t.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2 text-[13px] text-brand-ink/75">
                      <CheckCircle2 size={14} style={{ color: t.color }} className="shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ASÍ FUNCIONA */}
      <section id="como-funciona" className="px-5 md:px-8 py-16 md:py-24 scroll-mt-20 bg-white/60" data-testid="how-section">
        <div className="max-w-6xl mx-auto">
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="font-outfit font-bold text-3xl md:text-4xl tracking-tight text-brand-ink text-center mb-14">
            Así funciona <span style={{ color: BLUE }}>ClinPed</span> para ti
          </motion.h2>
          <div className="grid gap-4 md:grid-cols-9 md:items-start">
            {HOW_STEPS.map((s, i) => (
              <Fragment key={s.title}>
                <motion.div data-testid={`how-step-${i}`}
                  initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="md:col-span-1 flex md:flex-col items-center md:text-center gap-4 md:gap-3">
                  <div className="w-16 h-16 rounded-2xl bg-white border border-stone-100 shadow-sm flex items-center justify-center shrink-0" style={{ color: BLUE }}>
                    <s.icon size={26} />
                  </div>
                  <div>
                    <p className="font-outfit font-bold text-brand-ink text-sm">{i + 1}. {s.title}</p>
                    <p className="text-[13px] text-brand-ink/55 leading-relaxed mt-1">{s.desc}</p>
                  </div>
                </motion.div>
                {i < HOW_STEPS.length - 1 && (
                  <div className="hidden md:flex md:col-span-1 items-center justify-center pt-5 text-stone-300">
                    <ChevronRight size={22} />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ACCESOS DELEGADOS */}
      <section id="accesos" className="px-5 md:px-8 py-16 md:py-24 scroll-mt-20" data-testid="delegated-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-outfit font-bold text-3xl md:text-4xl tracking-tight text-brand-ink">Accesos Delegados</h2>
            <p className="mt-3 text-lg text-brand-ink/60">Tu equipo trabaja contigo, sin ver datos clínicos.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.05)] md:order-1">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${BLUE}14` }}><CalendarDays size={22} style={{ color: BLUE }} /></div>
              <h3 className="font-outfit font-bold text-brand-ink">Asistente</h3>
              <p className="text-sm text-brand-ink/60 mt-1 mb-3">Administra tu agenda y citas.</p>
              <ul className="space-y-1.5">
                {["Sin datos clínicos", "Sin ver expedientes", "Sin diagnósticos"].map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-[13px] text-brand-ink/75"><CheckCircle2 size={14} style={{ color: BLUE }} /> {b}</li>
                ))}
              </ul>
            </div>
            <div className="md:order-2 flex justify-center" data-testid="delegated-phone">
              <div className="w-56 rounded-[2rem] bg-brand-ink p-2 shadow-2xl">
                <div className="rounded-[1.6rem] bg-white px-5 py-6 text-center">
                  <div className="flex items-center justify-center mb-4"><Logo size={30} /></div>
                  <p className="text-sm font-semibold text-brand-ink">Escanea el QR</p>
                  <p className="text-xs text-brand-ink/50">o ingresa el PIN</p>
                  <QrCode size={104} className="mx-auto my-4 text-brand-ink" />
                  <p className="text-[11px] uppercase tracking-widest text-brand-ink/40">PIN</p>
                  <p className="font-mono text-2xl font-bold tracking-[0.3em] text-brand-ink">8572</p>
                  <p className="text-[11px] font-semibold mt-3" style={{ color: BLUE }}>Ver instrucciones</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.05)] md:order-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-rose-500/10"><HeartPulse size={22} className="text-rose-500" /></div>
              <h3 className="font-outfit font-bold text-brand-ink">Enfermería</h3>
              <p className="text-sm text-brand-ink/60 mt-1 mb-3">Captura signos vitales y somatometría.</p>
              <ul className="space-y-1.5">
                {["Sin datos clínicos", "Autollenado en consulta", "100% seguro"].map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-[13px] text-brand-ink/75"><CheckCircle2 size={14} className="text-rose-500" /> {b}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 flex items-center justify-center gap-2 text-center">
            <Lock size={18} style={{ color: BLUE }} />
            <p className="text-sm text-brand-ink/70"><span className="font-semibold text-brand-ink">Tú tienes el control total.</span> Genera, regenera o revoca los accesos cuando quieras.</p>
          </div>
        </div>
      </section>

      {/* NO SOLO PEDIATRÍA */}
      <section className="px-5 md:px-8 py-6">
        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          data-testid="adult-mode-band"
          className="max-w-5xl mx-auto rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 bg-brand-sage/12 border border-brand-sage/25">
          <div className="w-16 h-16 rounded-2xl bg-brand-sage flex items-center justify-center shrink-0">
            <Users size={30} className="text-white" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <span className="inline-block text-xs font-bold uppercase tracking-wide bg-brand-sage/20 text-brand-sageDark px-3 py-1 rounded-full mb-2">No solo pediatría</span>
            <h2 className="font-outfit font-bold text-xl md:text-2xl text-brand-ink">También atiende pacientes adultos, en automático</h2>
            <p className="mt-2 text-brand-ink/70">ClinPed <b>detecta automáticamente a los pacientes mayores de 18 años</b> y adapta el expediente al <b>modo adulto</b> —sin que cambies nada—. Un solo sistema para toda tu consulta, pediátrica y de adultos.</p>
          </div>
        </motion.div>
      </section>

      {/* SECURITY / COMPLIANCE */}
      <section className="px-5 md:px-8 py-6" data-testid="compliance-section">
        <div className="max-w-5xl mx-auto rounded-3xl p-8 md:p-10" style={{ backgroundColor: `${BLUE}0D` }}>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: BLUE }}>
              <ShieldCheck size={30} className="text-white" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <span className="inline-block text-xs font-bold uppercase tracking-wide bg-white px-3 py-1 rounded-full border border-stone-200 mb-2" style={{ color: BLUE }}>Cumplimiento normativo</span>
              <h2 className="font-outfit font-bold text-xl md:text-2xl text-brand-ink">Diseñado conforme a la <b>NOM-004</b> y la <b>NOM-024</b></h2>
              <p className="mt-2 text-brand-ink/70">ClinPed integra los <b>lineamientos técnicos</b> del expediente clínico electrónico mexicano: firma electrónica del médico, identificación del paciente por <b>CURP</b>, bitácora de accesos, interoperabilidad <b>HL7 CDA/XML</b> y cifrado de datos en tránsito. Tu información siempre bajo tu control.</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-6">
            {[
              [ShieldCheck, "NOM-004 · Expediente clínico"],
              [ShieldCheck, "NOM-024 · Sistema y datos"],
              [FileText, "Identificación por CURP"],
              [Lock, "Bitácora de accesos"],
              [FileText, "Interoperabilidad HL7 CDA/XML"],
              [Lock, "Datos cifrados"],
            ].map(([Icon, label]) => (
              <span key={label} className="flex items-center gap-1.5 text-xs font-bold text-brand-ink/70 bg-white px-3 py-2 rounded-full border border-stone-200">
                <Icon size={13} /> {label}
              </span>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-brand-ink/45">La certificación oficial ante la DGIS/Secretaría de Salud (SIRES) es un trámite administrativo del prestador de servicios de salud; ClinPed provee la plataforma técnica que la facilita.</p>
        </div>
      </section>

      {/* EN TODOS TUS DISPOSITIVOS */}
      <section id="dispositivos" className="px-5 md:px-8 py-16 md:py-24 scroll-mt-20 bg-white/60" data-testid="devices-section">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <p className="uppercase text-xs tracking-[0.25em] font-bold text-brand-terracotta mb-3">Multiplataforma</p>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl tracking-tight text-brand-ink">Tu consultorio, en todos tus dispositivos</h2>
            <p className="mt-4 text-lg text-brand-ink/70 leading-relaxed">Abre ClinPed desde tu computadora, tablet o celular. Todo se sincroniza en la nube en tiempo real: el mismo expediente, siempre a la mano.</p>
            <ul className="mt-7 space-y-3">
              {[
                ["Sin instalación", "Funciona en tu navegador; nada que descargar."],
                ["100% en la nube", "Tus expedientes disponibles donde estés."],
                ["Siempre actualizado", "Nuevas funciones sin actualizar nada."],
                ["Seguro y confiable", "Cifrado AES-256 y cumplimiento NOM-024."],
              ].map(([t, d], i) => (
                <li key={i} data-testid={`device-benefit-${i}`} className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="mt-0.5 shrink-0" style={{ color: BLUE }} />
                  <span><span className="font-semibold text-brand-ink">{t}.</span> <span className="text-brand-ink/65">{d}</span></span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
            className="relative">
            <img src="/store/tab_1_panel.png" alt="ClinPed en computadora" data-testid="device-desktop-img"
              className="w-full rounded-2xl shadow-2xl ring-1 ring-stone-900/5" />
            <img src="/store/play_1_dashboard.png" alt="ClinPed en celular" data-testid="device-phone-img"
              className="absolute -bottom-6 -left-4 w-[26%] rounded-2xl shadow-2xl ring-4 ring-white" />
          </motion.div>
        </div>
      </section>


      {/* TESTIMONIALS */}
      <section className="px-5 md:px-8 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-4">
            <p className="uppercase text-xs tracking-[0.25em] font-bold text-brand-terracotta mb-3">Lo que dicen los médicos</p>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl tracking-tight">Pediatras que ya trabajan mejor con ClinPed</h2>
          </div>
          <p className="text-center text-xs text-brand-ink/40 mb-10">Testimonios ilustrativos</p>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                data-testid={`testimonial-${i}`} className="bg-white rounded-2xl p-7 border border-stone-100 shadow-sm">
                <div className="flex gap-1 text-brand-sand mb-3">{[...Array(5)].map((_, j) => <Star key={j} size={15} fill="currentColor" />)}</div>
                <p className="text-brand-ink/80 leading-relaxed">“{t.text}”</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover ring-2 ring-brand-sand/40" />
                  <div><p className="font-outfit font-bold text-brand-ink text-sm">{t.name}</p><p className="text-xs text-brand-ink/50">{t.role}</p></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="precios" className="px-5 md:px-8 py-16 md:py-24 scroll-mt-20 bg-white/60">
        <div className="max-w-lg mx-auto text-center mb-10">
          <p className="uppercase text-xs tracking-[0.25em] font-bold text-brand-terracotta mb-3">Precios</p>
          <h2 className="font-outfit font-bold text-3xl md:text-4xl tracking-tight">Elige tu plan</h2>
          <p className="mt-3 text-brand-ink/60">Empieza con 20 días gratis. Suma el módulo Odontológico o cambia a Pro cuando quieras.</p>
        </div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6 items-start">
          {/* BÁSICO */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            whileHover={{ y: -6 }}
            data-testid="plan-basic" className="bg-white rounded-3xl border-2 border-blue-500 shadow-lg overflow-hidden transition-shadow hover:shadow-xl">
            <div className="h-1.5 bg-blue-500" />
            <div className="p-8 text-center">
              <span className="inline-block text-xs font-bold uppercase tracking-wide bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Básico</span>
              <p className="mt-4 font-outfit font-extrabold text-5xl text-brand-ink">$150<span className="text-xl font-semibold"> MXN</span></p>
              <p className="text-brand-ink/60 mt-1">por mes · todo el expediente incluido</p>
            </div>
            <div className="px-8 pb-8">
              <ul className="space-y-3">
                {PLAN_BASIC.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-brand-ink/80 text-sm"><CheckCircle2 size={18} className="text-blue-500 shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <button data-testid="pricing-cta-basic" onClick={startLogin}
                className="mt-8 w-full flex items-center justify-center gap-2 text-white font-outfit font-semibold py-3.5 rounded-xl bg-blue-500 hover:bg-blue-600 transition-all">
                Comenzar <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
          {/* ODONTOLÓGICO */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 }}
            whileHover={{ y: -6 }}
            data-testid="plan-dental" className="bg-white rounded-3xl border-2 border-teal-500 shadow-lg overflow-hidden transition-shadow hover:shadow-xl">
            <div className="h-1.5 bg-teal-500" />
            <div className="p-8 text-center">
              <span className="inline-block text-xs font-bold uppercase tracking-wide bg-teal-100 text-teal-700 px-3 py-1 rounded-full">Odontológico</span>
              <p className="mt-4 font-outfit font-extrabold text-5xl text-brand-ink">$170<span className="text-xl font-semibold"> MXN</span></p>
              <p className="text-brand-ink/60 mt-1">por mes · Básico + módulo dental</p>
            </div>
            <div className="px-8 pb-8">
              <ul className="space-y-3">
                {PLAN_DENTAL.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-brand-ink/80 text-sm"><CheckCircle2 size={18} className="text-teal-500 shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <button data-testid="pricing-cta-dental" onClick={startLogin}
                className="mt-8 w-full flex items-center justify-center gap-2 text-white font-outfit font-semibold py-3.5 rounded-xl bg-teal-500 hover:bg-teal-600 transition-all">
                Comenzar <ArrowRight size={18} />
              </button>
              <p className="mt-3 text-center text-xs text-brand-ink/50">Activa el módulo dental dentro de la app tras crear tu cuenta.</p>
            </div>
          </motion.div>
          {/* PRO */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            whileHover={{ y: -8 }}
            data-testid="plan-pro" className="relative bg-white rounded-3xl border-2 shadow-2xl overflow-hidden md:scale-[1.03]" style={{ borderColor: "#C77B58" }}>
            <span className="absolute top-4 right-4 z-10 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-white px-3 py-1 rounded-full shadow" style={{ backgroundColor: "#C77B58" }}><Star size={12} className="fill-white" /> Más popular</span>
            <div className="p-8 text-center text-white" style={{ backgroundColor: "#C77B58" }}>
              <span className="inline-block text-xs font-bold uppercase tracking-wide bg-white/20 px-3 py-1 rounded-full">Pro</span>
              <p className="mt-4 font-outfit font-extrabold text-5xl">$400<span className="text-xl font-semibold"> MXN</span></p>
              <p className="text-white/80 mt-1">por mes · IA + equipo de asistencia</p>
            </div>
            <div className="p-8">
              <ul className="space-y-3">
                {PLAN_PRO.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-brand-ink/80 text-sm"><CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{ color: "#C77B58" }} /> {f}</li>
                ))}
              </ul>
              <button data-testid="pricing-cta-pro" onClick={startLogin}
                className="mt-8 w-full flex items-center justify-center gap-2 text-white font-outfit font-semibold py-3.5 rounded-xl shadow-lg hover:-translate-y-1 transition-all" style={{ backgroundColor: "#C77B58" }}>
                Probar Pro <ArrowRight size={18} />
              </button>
              <p className="mt-3 text-center text-xs text-brand-ink/50">Activa el Pro dentro de la app tras crear tu cuenta.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" className="px-5 md:px-8 py-16 md:py-24 scroll-mt-20">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="uppercase text-xs tracking-[0.25em] font-bold text-brand-terracotta mb-3">Contacto</p>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl tracking-tight">¿Tienes dudas? Escríbenos</h2>
            <p className="mt-4 text-brand-ink/70">Déjanos tus datos y te contactaremos para resolver tus preguntas o agendar una demostración personalizada.</p>
            <a href="mailto:soporteclinped@expedienteelectronicoclinped.com" data-testid="contact-email" className="mt-6 inline-flex items-center gap-2 font-semibold" style={{ color: BLUE }}>
              <Mail size={18} /> soporteclinped@expedienteelectronicoclinped.com
            </a>
          </div>
          <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-3xl p-7 border border-stone-100 shadow-sm space-y-4">
            <input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Nombre completo *"
              className="w-full rounded-xl bg-stone-50 border border-transparent focus:border-stone-300 focus:bg-white px-4 py-3 text-sm outline-none transition-colors" />
            <input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Correo electrónico *"
              className="w-full rounded-xl bg-stone-50 border border-transparent focus:border-stone-300 focus:bg-white px-4 py-3 text-sm outline-none transition-colors" />
            <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="Teléfono (opcional)"
              className="w-full rounded-xl bg-stone-50 border border-transparent focus:border-stone-300 focus:bg-white px-4 py-3 text-sm outline-none transition-colors" />
            <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={3} placeholder="Mensaje"
              className="w-full rounded-xl bg-stone-50 border border-transparent focus:border-stone-300 focus:bg-white px-4 py-3 text-sm outline-none transition-colors resize-none" />
            <button type="submit" data-testid="contact-submit" disabled={sending}
              className="w-full flex items-center justify-center gap-2 text-white font-outfit font-semibold py-3.5 rounded-xl shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60"
              style={{ backgroundColor: BLUE }}>
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />} {sending ? "Enviando…" : "Enviar mensaje"}
            </button>
          </form>
        </div>
      </section>

      {/* CTA FINAL OSCURO */}
      <section className="px-5 md:px-8 py-10" data-testid="final-cta-section">
        <div className="max-w-5xl mx-auto rounded-3xl px-6 py-14 md:py-16 text-center" style={{ backgroundColor: "#0b1220" }}>
          <div className="flex items-center justify-center gap-2 mb-5">
            {ECOSYSTEM.map((t) => (
              <span key={t.name} className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${t.color}26` }}>
                <t.icon size={18} style={{ color: t.color }} />
              </span>
            ))}
          </div>
          <h2 className="font-outfit font-bold text-3xl md:text-4xl tracking-tight text-white">
            El ecosistema pediátrico<br /><span style={{ color: "#38bdf8" }}>más completo e inteligente.</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-3xl mx-auto mt-10 mb-9">
            {[
              [Lock, "Seguro", "Cifrado de nivel hospitalario"],
              [ShieldCheck, "Confiable", "Respaldo automático y en la nube"],
              [MonitorSmartphone, "Siempre contigo", "Web, tablet y móvil"],
              [LifeBuoy, "Soporte real", "De médicos para médicos"],
            ].map(([Icon, t, d], i) => (
              <div key={i} className="text-center">
                <Icon size={22} className="mx-auto mb-2" style={{ color: "#38bdf8" }} />
                <p className="text-white font-semibold text-sm">{t}</p>
                <p className="text-white/50 text-xs mt-0.5 leading-snug">{d}</p>
              </div>
            ))}
          </div>
          <button data-testid="final-cta-trial" onClick={startLogin}
            className="inline-flex items-center gap-2 text-white font-outfit font-bold text-lg px-8 py-4 rounded-2xl shadow-xl hover:-translate-y-0.5 transition-transform"
            style={{ background: "linear-gradient(135deg,#0ea5e9,#0d9488)" }}>
            Prueba gratis 20 días <ArrowRight size={20} />
          </button>
          <p className="text-white/45 text-xs mt-4">Sin tarjeta de crédito. Cancela cuando quieras.</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-brand-ink text-white px-5 md:px-8 py-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between gap-8">
          <div className="max-w-sm">
            <Logo size={40} className="[&_span]:text-white [&_.text-brand-ink]:text-white" />
            <p className="mt-4 text-white/60 text-sm">Expediente clínico electrónico pediátrico inteligente, conforme a la NOM-004. Uso profesional para pediatras.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-10">
            <div>
              <p className="font-outfit font-semibold text-sm mb-3">Producto</p>
              <div className="flex flex-col gap-2 text-sm text-white/60">
                <button onClick={() => document.getElementById("caracteristicas")?.scrollIntoView({ behavior: "smooth" })} className="text-left hover:text-white">Características</button>
                <button onClick={() => document.getElementById("precios")?.scrollIntoView({ behavior: "smooth" })} className="text-left hover:text-white">Precios</button>
                <button data-testid="footer-login" onClick={startLogin} className="text-left hover:text-white">Iniciar sesión</button>
              </div>
            </div>
            <div>
              <p className="font-outfit font-semibold text-sm mb-3">Legal y contacto</p>
              <div className="flex flex-col gap-2 text-sm text-white/60">
                <a href="/privacy" className="hover:text-white" data-testid="footer-privacy">Aviso de Privacidad</a>
                <a href="/terms" className="hover:text-white" data-testid="footer-terms">Términos de uso</a>
                <a href="mailto:soporteclinped@expedienteelectronicoclinped.com" className="hover:text-white">soporteclinped@expedienteelectronicoclinped.com</a>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-white/10 text-center text-white/40 text-xs">
          <p className="mb-3 leading-relaxed max-w-3xl mx-auto" data-testid="verification-legend">
            Los médicos con el sello "Profesional verificado por ClinPed" completaron su registro y ClinPed verificó la coincidencia de sus datos con el Registro Nacional de Profesionistas.
          </p>
          © {new Date().getFullYear()} ClinPed · Dr. Néstor Gibran López Díaz. Todos los derechos reservados.
        </div>
      </footer>

      {/* Botón flotante de WhatsApp */}
      <a
        href="https://wa.me/525620417566?text=Hola,%20me%20interesa%20conocer%20m%C3%A1s%20sobre%20ClinPed"
        target="_blank" rel="noopener noreferrer"
        data-testid="whatsapp-float"
        aria-label="Escríbenos por WhatsApp"
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-[#25D366] text-white font-outfit font-semibold pl-4 pr-5 py-3 rounded-full shadow-xl hover:-translate-y-1 hover:shadow-2xl transition-all"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="shrink-0">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.548 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        WhatsApp
      </a>
    </div>
  );
}
